export function createQuantityFormatter(adapter, named = {}) {
    return {
        format(value, options = {}) {
            if (!adapter.isFinite(value))
                throw new TypeError("cannot format a non-finite quantity");
            const resolved = typeof options === "string" ? named[options] : options;
            if (!resolved)
                throw new TypeError(`unknown quantity format: ${options}`);
            return formatEncoded(adapter.codec.serialize(value), resolved);
        },
    };
}
export function formatEncoded(encoded, options = {}) {
    const digits = options.significantDigits ?? 6;
    if (!Number.isSafeInteger(digits) || digits < 1 || digits > 20) {
        throw new RangeError("significantDigits must be an integer from 1 through 20");
    }
    const parts = scientificParts(encoded);
    const notation = options.notation ?? (parts && Math.abs(parts.exponent) >= 6 ? "scientific" : "plain");
    if (!parts)
        return encoded;
    if (notation === "plain")
        return formatPlain(encoded, options);
    const exponent = notation === "engineering" ? Math.floor(parts.exponent / 3) * 3 : parts.exponent;
    const coefficient = parts.coefficient * 10 ** (parts.exponent - exponent);
    let rendered = coefficient.toPrecision(digits);
    if (options.trimTrailingZeros !== false)
        rendered = trimZeros(rendered);
    return `${parts.negative ? "-" : ""}${rendered}e${exponent >= 0 ? "+" : ""}${exponent}`;
}
function formatPlain(encoded, options) {
    const places = options.plainDecimalPlaces ?? 0;
    if (!Number.isSafeInteger(places) || places < 0 || places > 20) {
        throw new RangeError("plainDecimalPlaces must be an integer from 0 through 20");
    }
    const numeric = Number(encoded);
    if (!Number.isFinite(numeric))
        return encoded;
    const fixed = numeric.toFixed(places);
    const rendered = options.trimTrailingZeros === false ? fixed : trimZeros(fixed);
    return /^-0(?:\.0*)?$/.test(rendered) ? rendered.slice(1) : rendered;
}
function scientificParts(encoded) {
    const match = /^(-)?(\d+)(?:\.(\d+))?(?:e([+-]?\d+))?$/i.exec(encoded);
    if (!match)
        return undefined;
    const integer = match[2] ?? "0";
    const fraction = match[3] ?? "";
    const all = `${integer}${fraction}`;
    const nonzero = all.search(/[1-9]/);
    if (nonzero < 0)
        return { negative: false, coefficient: 0, exponent: 0 };
    const explicit = Number(match[4] ?? 0);
    const exponent = explicit + integer.length - nonzero - 1;
    const significant = all.slice(nonzero, nonzero + 17);
    const coefficient = Number(`${significant[0]}.${significant.slice(1)}`);
    return { negative: match[1] === "-", coefficient, exponent };
}
function trimZeros(value) {
    const [coefficient, exponent] = value.split("e");
    const trimmed = (coefficient ?? value).replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
    return exponent === undefined ? trimmed : `${trimmed}e${exponent}`;
}
//# sourceMappingURL=numbers.js.map